// libs
import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { TextStyle, Avatar, Card } from '@superops/ui-design-system';
// env config
import envConfig from '@nile/home/src/Config/env';
// styled components
// reusables
import { DateTimeFormatter } from '@nile/components';
// graphql
import { useQuery, GET_ALL_APPROVAL_STATUS } from '@nile/graphql';
// interfaces
import {
  UserData,
  MessageData,
  CustomBubbleProps
} from '@nile/mangifera/src/Pebbles/Interfaces/interfaces';
import { StringOrNumber } from '@nile/mangifera/src/Pebbles/Types/types';
// constants
import {
  lockaliseKeys,
  stateIconInfoMap
} from '@nile/mangifera/src/Constants/chatFeed';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SVGIcon } from '@superops/super-icons';

// styles
const StyledNote = styled.div`
  background: var(--sops-coversation-bubble-bg);
  border-radius: 20px 20px;
  width: 80%;
  margin-left: 12px;
  padding: 10px 15px 15px 15px;
  opacity: 1;
  transition: opacity 200ms linear;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.102874);

  .comment {
    background-color: #f2f2f7;
    border-radius: 10px;
    padding: 10px;
  }

  .ml-8 {
    margin-left: 8px;
  }
`;

const EmptyDiv = styled.div``;

// utils
const getApprovalStateTime = (
  statuses: any,
  status: string,
  sentOn: Date | null,
  actionTakenOn: Date | null
): Date | null => {
  switch (statuses[status]) {
    case 'approved':
    case 'rejected':
    case 'needMoreInfo':
      return actionTakenOn;
    case 'sent':
    case 'cancelled':
      return sentOn;
    default:
      return null;
  }
};

const getAuditApprovalStateTime = (
  statuses: any,
  status: string,
  time: Date | null
): Date | null => {
  switch (statuses[status]) {
    case 'approved':
    case 'rejected':
    case 'needMoreInfo':
    case 'sent':
    case 'cancelled':
      return time;
    default:
      return null;
  }
};

interface ApprovalFeedData {
  approvalId: StringOrNumber;
  approver: UserData;
  status: string;
  comment?: string;
  sentOn: Date | null;
  actionTakenOn: Date | null;
}

interface ApprovalMessageData extends MessageData {
  data: ApprovalFeedData;
}

interface ApprovalBubbleProps extends CustomBubbleProps {
  message: ApprovalMessageData;
}

const ApprovalBubble: FC<ApprovalBubbleProps> = (props) => {
  const [statuses, setStatuses] = useState({});
  const flags = useFlags();

  const { message } = props;
  const approverName: string = message?.user?.name;
  const comment: string = message?.message;
  const status: string = message?.messageType;
  const time: Date | null = getAuditApprovalStateTime(
    statuses,
    status,
    message?.time
  );

  // Similar styling structure to ConversationBubbleNew
  const bubbleWrapperClassNames = ['flex g10 bubble-wrapper', 'px10']
    .filter(Boolean)
    .join(' ');

  const bubbleOuterClassNames = ['w100'].filter(Boolean).join(' ');

  const bubblePadding = 'pt20 pb20 px10';

  const bubbleClassNames = ['word-break br20', bubblePadding]
    .filter(Boolean)
    .join(' ');

  const headerClassNames = ['bubble-header fsize12 flex actr fx-sb fg1']
    .filter(Boolean)
    .join(' ');

  const { data, loading: statusLoading, error: statusError } = useQuery(
    GET_ALL_APPROVAL_STATUS,
    {
      onCompleted(res) {
        const approvalStatuses = res.getAllApprovalStatus;
        setStatuses({
          [approvalStatuses.find((el) => el.state === 'CREATE').name]: 'sent',
          [approvalStatuses.find((el) => el.state === 'APPROVE').name]:
            'approved',
          [approvalStatuses.find((el) => el.state === 'REJECT').name]:
            'rejected',
          [approvalStatuses.find((el) => el.state === 'CANCEL').name]:
            'cancelled',
          [approvalStatuses.find((el) => el.state === 'NEED_MORE_INFORMATION')
            .name]: 'needMoreInfo'
        });
      }
    }
  );
  return (
    <div className={bubbleWrapperClassNames} id="approval-bubble-wrapper">
      <div id="conversation-bubble-outer" className={bubbleOuterClassNames}>
        <Card variant="card" padding="sizeExtraSmall" border="none">
          <div id="conversation-bubble-inner" className={bubbleClassNames}>
            <div className="flex g10 align-items-center">
              <div className="mr8">
                <Avatar
                  size={'extraSmall'}
                  source={`${envConfig.imageRoot}svg/notification.svg`}
                />
              </div>
              <div className={headerClassNames}>
                <div className="added-by flex g5 actr">
                  <div className="flex mr-5">
                    <SVGIcon
                      name={stateIconInfoMap[statuses[status]]?.icon}
                      color={stateIconInfoMap[statuses[status]]?.color}
                      size="sm"
                    />
                  </div>
                  <div className="flex g3">
                    <div>
                      {lockaliseKeys[statuses[status]] && (
                        <FormattedMessage
                          id={lockaliseKeys[statuses[status]]}
                        />
                      )}
                    </div>
                    {statuses[status] && statuses[status] !== 'cancelled' && (
                      <div className="flex g5 actr">
                        <div>
                          <Avatar
                            name={approverName}
                            initials={`${message?.user?.firstName?.[0] ||
                              'NA'}`}
                            size="extraSmall"
                          />
                        </div>
                        <div>{approverName}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex g3 right-container actr">
                  <TextStyle
                    variation="subdued"
                    title={<DateTimeFormatter type="dateTime" date={time} />}
                  >
                    <div className="flex">
                      <DateTimeFormatter type="dateTime" date={time} />
                    </div>
                  </TextStyle>
                </div>
              </div>
            </div>

            {comment && (
              <div className="w100 wm-700">
                <Card noShadow variant="secondaryCard">
                  <div className="comment p10">{comment}</div>
                </Card>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ApprovalBubble;
